"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Link from "next/link";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await fetch("/api/auth/forgot-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || "Er is een fout opgetreden");
        return;
      }

      setIsSubmitted(true);
      toast.success("Reset link verzonden");
    } catch {
      toast.error("Er is een fout opgetreden");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* Left Column - Form */}
      <div className="flex items-center justify-center bg-white p-8">
        <div className="w-full max-w-md space-y-8">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-primary" />
            <span className="text-xl font-semibold">MijnPatient</span>
          </Link>

          {isSubmitted ? (
            // Success State
            <div className="space-y-6">
              <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tight">
                  Check je email
                </h1>
                <p className="text-neutral-600">
                  We hebben een reset link gestuurd naar{" "}
                  <strong>{email}</strong>. Klik op de link in de email om je
                  wachtwoord opnieuw in te stellen.
                </p>
              </div>

              <div className="rounded-lg border border-neutral-200 bg-neutral-50 p-4">
                <p className="text-sm text-neutral-600">
                  Geen email ontvangen? Controleer je spam folder of probeer het
                  opnieuw.
                </p>
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  setIsSubmitted(false);
                  setEmail("");
                }}
              >
                Probeer opnieuw
              </Button>

              <div className="text-center text-sm text-neutral-600">
                <Link
                  href="/login"
                  className="font-medium text-primary hover:underline"
                >
                  Terug naar inloggen
                </Link>
              </div>
            </div>
          ) : (
            // Form State
            <>
              <div className="space-y-2">
                <h1 className="text-3xl font-bold tracking-tight">
                  Wachtwoord vergeten?
                </h1>
                <p className="text-neutral-600">
                  Geen probleem. Voer je email in en we sturen je een link om je
                  wachtwoord opnieuw in te stellen.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="jouw@email.nl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLoading}
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  size="lg"
                  disabled={isLoading}
                >
                  {isLoading ? "Versturen..." : "Verstuur reset link"}
                </Button>
              </form>

              <div className="text-center text-sm text-neutral-600">
                <Link
                  href="/login"
                  className="font-medium text-primary hover:underline"
                >
                  Terug naar inloggen
                </Link>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Column - Hero */}
      <div className="hidden bg-primary p-12 text-white lg:flex lg:flex-col lg:justify-center">
        <div className="space-y-8">
          <div className="space-y-4">
            <h2 className="text-4xl font-bold leading-tight">
              Veilig en betrouwbaar
            </h2>
            <p className="text-lg text-primary-foreground/80">
              We nemen de beveiliging van je account serieus. Je ontvangt een
              veilige reset link via email.
            </p>
          </div>

          {/* Security Features */}
          <div className="space-y-4">
            <Card className="border-none bg-white/10 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="rounded-lg bg-white/20 p-3">
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold">Beveiligde reset link</h3>
                    <p className="mt-1 text-sm text-primary-foreground/80">
                      De link is 24 uur geldig en kan maar één keer gebruikt
                      worden
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none bg-white/10 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="rounded-lg bg-white/20 p-3">
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold">Email notificatie</h3>
                    <p className="mt-1 text-sm text-primary-foreground/80">
                      Je ontvangt direct een email met instructies
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-none bg-white/10 backdrop-blur-sm">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="rounded-lg bg-white/20 p-3">
                    <svg
                      className="h-6 w-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold">Geen zorgen</h3>
                    <p className="mt-1 text-sm text-primary-foreground/80">
                      Je bestaande gegevens blijven veilig opgeslagen
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Help Section */}
          <div className="rounded-lg border border-white/20 bg-white/10 p-6 backdrop-blur-sm">
            <h3 className="font-semibold">Hulp nodig?</h3>
            <p className="mt-2 text-sm text-primary-foreground/80">
              Neem contact op met ons support team via{" "}
              <a
                href="mailto:support@mijnpatient.nl"
                className="font-medium underline"
              >
                support@mijnpatient.nl
              </a>
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 flex items-center justify-between text-sm text-primary-foreground/60">
          <p>Copyright © 2025 MijnPatient</p>
          <Link href="/privacy" className="hover:text-white">
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
